import {message as staticMessage, notification as staticNotification, Modal as staticModal} from "antd";

/**
 * Themed replacements for antd's static `message`, `notification` and `Modal` helpers.
 *
 * From v5 the static versions render outside React's tree, so they never see the
 * ConfigProvider theme — antd warns about exactly this. The supported fix is `App.useApp()`,
 * but that is a hook and these are called from 70-odd plain callbacks.
 *
 * So a single `<AntdFeedbackBridge/>` inside `<App>` publishes the context-aware instances
 * here, and call sites import from this module instead of from antd. Only the import line
 * changes; every `message.success(...)` keeps working, now themed.
 *
 * Before the bridge mounts, calls fall through to the static versions rather than being
 * dropped — a toast fired during boot is still better unthemed than missing.
 */

type Feedback = {
  message: any;
  notification: any;
  modal: any;
};

const fallback: Feedback = {
  message: staticMessage,
  notification: staticNotification,
  modal: staticModal,
};

let current: Feedback = fallback;

export const setAntdFeedback = (next: Partial<Feedback>) => {
  current = {...fallback, ...next};
};

const forward = (channel: keyof Feedback, method: string) =>
  (...args: any[]) => (current[channel] as any)[method](...args);

export const message = {
  success: forward("message", "success"),
  error: forward("message", "error"),
  warning: forward("message", "warning"),
  info: forward("message", "info"),
  loading: forward("message", "loading"),
  open: forward("message", "open"),
  destroy: forward("message", "destroy"),
};

export const notification = {
  success: forward("notification", "success"),
  error: forward("notification", "error"),
  warning: forward("notification", "warning"),
  info: forward("notification", "info"),
  open: forward("notification", "open"),
  destroy: forward("notification", "destroy"),
};

export const modal = {
  confirm: forward("modal", "confirm"),
  info: forward("modal", "info"),
  success: forward("modal", "success"),
  error: forward("modal", "error"),
  warning: forward("modal", "warning"),
};
